import { Star, Phone, ChevronRight, CheckCircle2, CheckCircle, AlertCircle, Loader2, Send } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useHead } from '@unhead/react';
import hero1 from '../../images/VIP House Movers Removal Team with Fleet.png';
import hero2 from '../../images/VIP House Movers Banner AD.png';
import hero3 from '../../images/VIP House Movers Home Survey.png';
import hero4 from '../../images/vip-house-movers-team-carrying-outdoor-furniture.jpeg';

// Web3Forms — free, no backend needed. Get your free access key at https://web3forms.com
// Same key as the Quote and Contact pages
const WEB3FORMS_KEY = 'ff8dcc3a-1d86-4e87-8503-c40947d75565';

const heroImages = [
  hero1,
  hero2,
  hero3,
  hero4,
];

const heroImageAlts = [
  'VIP House Movers removal team with professional fleet of vans for house moving services',
  'VIP House Movers professional removal and relocation banner advertisement',
  'VIP House Movers home survey and house assessment service',
  'VIP House Movers team carefully carrying outdoor furniture during a garden move',
];

export function Hero() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // Preload first image for Core Web Vitals (LCP)
    const img = new Image();
    img.src = heroImages[0];
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  useHead({
    link: [
      { rel: 'preload', as: 'image', href: heroImages[0] },
      { rel: 'preload', as: 'image', href: heroImages[1] },
      { rel: 'preload', as: 'image', href: heroImages[2] },
    ],
  });

  const inputClass = (field: string) =>
    `w-full px-4 py-3 border rounded-lg outline-none transition-colors text-sm focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37] ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
    }`;

  function validate() {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Please enter your name.';
    if (!form.email.trim()) e.email = 'Please enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Please enter a valid email address.';
    if (!form.message.trim()) e.message = 'Please enter your message.';
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New Contact Enquiry from ${form.name} — VIP House Movers`,
          from_name: 'VIP House Movers Website',
          replyto: form.email,
          name: form.name,
          email: form.email,
          phone: form.phone || 'Not provided',
          message: form.message,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Something went wrong.');
      setStatus('success');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(
        err instanceof Error
          ? err.message
          : 'Unable to send your message. Please call us on 0121 752 7107.'
      );
    }
  }

  return (
    <div ref={heroRef} className="relative bg-gray-900 border-b-4 border-[#0F4A4E] overflow-hidden">
      <div className="absolute inset-0 z-0">
        {heroImages.map((img, index) => (
          <img
            key={img}
            src={img}
            alt={heroImageAlts[index]}
            width={1920}
            height={1080}
            decoding={index === 0 ? 'auto' : 'async'}
            loading={index === 0 ? 'eager' : 'lazy'}
            fetchPriority={index === 0 ? 'high' : 'auto'}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
              index === currentImageIndex ? 'opacity-30' : 'opacity-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/30 via-gray-900/20 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E7F2F2] text-[#0F4A4E] border border-[#B7D1D2] mb-6">
              <Star className="w-4 h-4 fill-current" />
              <span className="text-sm font-medium tracking-wide uppercase">Wolverhampton's Premium Movers</span>
            </div>

            <h1 className="text-4xl md:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6 tracking-tight">
              House Removals in Wolverhampton, <span className="text-[#0F4A4E]">stress-free.</span>
            </h1>

            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-xl">
              Premium house removal company in Wolverhampton. Professional, fully insured home and office removals across the West Midlands. Eco-friendly solutions for your move.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/quote" className="inline-flex justify-center items-center gap-2 bg-[#0F4A4E] hover:bg-[#0C3D41] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 transform hover:scale-105 shadow-[0_0_20px_rgba(15,74,78,0.28)]">
                <Phone className="w-5 h-5" />
                Get a Free Quote
              </Link>
              <Link to="/services" className="inline-flex justify-center items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 backdrop-blur-sm">
                Our Services
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="mt-12 flex items-center gap-6 text-gray-400 text-sm font-medium">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#0F4A4E]" />
                Fully Insured
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#0F4A4E]" />
                Expert Packing
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#0F4A4E]" />
                Local & National
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="w-full max-w-md mx-auto lg:ml-auto">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 sm:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Get Your Free Quote</h2>
              <p className="text-sm text-gray-500 mb-6">Fill in the form below and we'll get back to you shortly — usually within 30 minutes.</p>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-10 text-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Message Sent!</h3>
                  <p className="text-gray-600 max-w-sm text-sm">
                    Thank you for contacting VIP House Movers. We've sent a confirmation to your email and will be in touch very soon.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-2 text-sm text-[#D4AF37] font-semibold hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className={inputClass('name')}
                      placeholder="Your full name"
                      autoComplete="name"
                    />
                    {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className={inputClass('email')}
                      placeholder="your@email.com"
                      autoComplete="email"
                    />
                    {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      className={inputClass('phone')}
                      placeholder="07700 900000"
                      autoComplete="tel"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={4}
                      value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      className={inputClass('message')}
                      placeholder="Tell us about your move..."
                    />
                    {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
                  </div>

                  {status === 'error' && (
                    <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{errorMsg}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-gray-900 font-bold py-3.5 rounded-xl hover:bg-yellow-500 transition-all shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-gray-400">
                    Your message is sent directly to info@viphousemovers.com. No obligation — 100% free.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
